import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { FadeUp } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

const Card = ({ p, i }) => (
  <FadeUp delay={i * 0.07}>
    <Link
      to={`/blog/${p.slug}`}
      data-testid={`blog-card-${p.slug}`}
      className="group block overflow-hidden rounded-[1.5rem] bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgb(0,0,0,0.08)]"
    >
      <div className="h-52 overflow-hidden bg-sand">
        {p.image_url ? (
          <img
            src={p.image_url}
            alt={p.title}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <img src="/logo-black.webp" alt="SanCub" className="h-8 w-auto opacity-20" />
          </div>
        )}
      </div>
      <div className="p-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/40">{fmtDate(p.published_at)}</p>
        <h2 className="mt-2 font-display text-xl font-medium leading-snug tracking-tight text-ink">{p.title}</h2>
        {p.meta_description && (
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-ink/60">{p.meta_description}</p>
        )}
        <span className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-ink">
          Read
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  </FadeUp>
);

const Skeleton = () => (
  <div className="overflow-hidden rounded-[1.5rem] bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="blog-skeleton">
    <div className="h-52 animate-pulse bg-ink/5" />
    <div className="space-y-3 p-6">
      <div className="h-3 w-24 animate-pulse rounded bg-ink/5" />
      <div className="h-5 w-3/4 animate-pulse rounded bg-ink/5" />
      <div className="h-3 w-full animate-pulse rounded bg-ink/5" />
    </div>
  </div>
);

const Blog = () => {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    document.title = "Journal — SanCub";
    axios
      .get(`${API}/blog`)
      .then(({ data }) => setPosts(data))
      .catch(() => setPosts([]));
    return () => {
      document.title = "SanCub — Architectural Tapware & Bathroom Fixtures";
    };
  }, []);

  return (
    <main className="pb-28 pt-32 md:pt-40" data-testid="blog-page">
      <div className="mx-auto max-w-[1500px] px-6 md:px-10">
        <FadeUp>
          <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-ink/50">SanCub Journal</p>
          <h1 className="mt-4 max-w-2xl font-display text-5xl font-medium leading-[1.02] tracking-tight text-ink md:text-7xl" data-testid="blog-headline">
            Ideas, care guides &amp; notes.
          </h1>
          <p className="mt-5 max-w-md text-base leading-relaxed text-ink/60">
            Practical guides on choosing, styling and caring for tapware and bathroom fixtures.
          </p>
        </FadeUp>

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
          {posts === null ? (
            [0, 1, 2].map((i) => <Skeleton key={i} />)
          ) : posts.length === 0 ? (
            <FadeUp className="sm:col-span-2 lg:col-span-3">
              <div className="rounded-[2rem] bg-white p-14 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="blog-empty">
                <img src="/logo-black.webp" alt="SanCub" className="mx-auto h-8 w-auto opacity-25" />
                <p className="mt-5 font-display text-2xl font-medium tracking-tight text-ink">Articles are on their way.</p>
                <p className="mt-2 text-sm text-ink/50">New guides will appear here soon. In the meantime, browse the collections.</p>
                <Link
                  to="/collections"
                  data-testid="blog-empty-cta"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-[13px] font-semibold text-white transition duration-300 hover:scale-[1.02] hover:bg-ink/90"
                >
                  View collections <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </FadeUp>
          ) : (
            posts.map((p, i) => <Card key={p.slug} p={p} i={i} />)
          )}
        </div>
      </div>
    </main>
  );
};

export default Blog;
